import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import FaqJsonLd from "@/components/FaqJsonLd";
import { Users, MapPin, Handshake, Star, Package, Lock, Building2, ArrowRight, Network, CheckCircle2 } from "lucide-react";

const Stat = ({ n, label, icon: Icon }) => (
  <div className="bg-white/90 border border-slate-200 rounded-2xl p-5 text-center">
    <Icon className="mx-auto text-[#E05638] mb-2" size={22} />
    <div className="text-3xl font-extrabold text-slate-900">{n}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
  </div>
);

export default function NetworkPublic() {
  useSeo({
    title: "Réseau de professionnels locaux en Eure-et-Loir | PROXIreduc-28",
    description: "Découvrez le réseau de professionnels, artisans et commerçants de Chartres, Dreux et de l'Eure-et-Loir. Trouvez un pro de confiance près de chez vous sur PROXIreduc-28.",
    canonicalPath: "/reseau-pro",
    image: "/og-image.jpg",
  });
  const { user } = useAuth();
  const isPro = !!user && (user.role === "professional" || (user.roles || []).includes("professional"));
  const [data, setData] = useState({ total_pros: 0, total_cities: 0, total_sectors: 0, sample: [] });
  const [sectors, setSectors] = useState([]);
  useEffect(() => {
    api.get("/public/network/teaser").then((r) => setData(r.data)).catch(() => {});
    api.get("/categories").then((r) => setSectors((r.data?.categories || r.data || []).slice(0, 10))).catch(() => {});
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen">
      <FaqJsonLd faq={[
        { q: "Comment trouver un professionnel local en Eure-et-Loir ?", a: "Parcourez le réseau PROXIreduc-28 par activité ou par ville (Chartres, Dreux, Châteaudun…) pour trouver un artisan, commerçant ou prestataire de confiance près de chez vous." },
        { q: "Les professionnels du réseau sont-ils vérifiés ?", a: "Chaque fiche professionnelle est validée avant publication afin d'assurer la fiabilité du réseau local." },
        { q: "Puis-je demander un devis à un professionnel du réseau ?", a: "Oui, vous pouvez demander gratuitement un devis aux professionnels référencés sur PROXIreduc-28." },
      ]} />
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#ffb59e] mb-4"><Users size={14} />Réseau Pro local</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold leading-tight max-w-3xl">Le réseau des entreprises d'Eure-et-Loir</h1>
          <p className="text-slate-300 mt-4 max-w-2xl text-base sm:text-lg">Trouvez des partenaires, échangez des recommandations, identifiez des fournisseurs et créez des collaborations entre pros du 28. Gratuit pour les commerçants partenaires.</p>
          {isPro ? (
            <div className="mt-7">
              <div className="inline-flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-4 py-1.5 mb-4" data-testid="netpub-already-pro">
                <CheckCircle2 size={15} /> Vous êtes déjà partenaire — pas besoin de vous réinscrire.
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/pro/reseau" className="inline-flex items-center gap-2 bg-[#E05638] hover:bg-[#c94a2f] text-white font-semibold px-6 py-3 rounded-xl transition-colors" data-testid="netpub-open-network"><Network size={16} /> Accéder à mon réseau pro <ArrowRight size={16} /></Link>
                <Link to="/pro/reseau/profil" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-colors" data-testid="netpub-edit-profile">Compléter mon profil réseau</Link>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/inscription?role=professional" className="inline-flex items-center gap-2 bg-[#E05638] hover:bg-[#c94a2f] text-white font-semibold px-6 py-3 rounded-xl transition-colors" data-testid="netpub-join">Rejoindre gratuitement <ArrowRight size={16} /></Link>
              <Link to="/pour-les-professionnels" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-colors">En savoir plus</Link>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 -mt-10 relative z-10">
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <Stat n={data.total_pros} label="professionnels" icon={Building2} />
          <Stat n={data.total_cities} label="villes du 28" icon={MapPin} />
          <Stat n={data.total_sectors} label="secteurs d'activité" icon={Package} />
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-1">Un aperçu du réseau</h2>
        <p className="text-slate-500 text-sm mb-6">{isPro ? "Ouvrez votre espace pour accéder à l'annuaire complet, aux connexions et aux recommandations." : "Créez votre compte pro gratuit pour débloquer l'annuaire complet, les connexions et les recommandations."}</p>
        <div className="relative">
          <div className="grid sm:grid-cols-3 gap-4">
            {(data.sample || []).map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4 select-none" style={{ filter: "blur(3px)" }}>
                <div className="flex items-center gap-3">
                  {s.logo ? <img src={s.logo} alt="" className="w-11 h-11 rounded-xl object-cover" /> : <div className="w-11 h-11 rounded-xl bg-slate-100" />}
                  <div><div className="font-semibold text-slate-900">{s.name}</div><div className="text-xs text-slate-500">{s.city} · {s.category_name}</div></div>
                </div>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            {isPro ? (
              <Link to="/pro/reseau" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-6 py-3 rounded-xl shadow-lg" data-testid="netpub-unlock"><Network size={16} />Ouvrir mon réseau</Link>
            ) : (
              <Link to="/inscription?role=professional" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-6 py-3 rounded-xl shadow-lg" data-testid="netpub-unlock"><Lock size={16} />Débloquer le réseau</Link>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-12">
          {[{ i: Handshake, t: "Trouvez des partenaires", d: "Recherchez par secteur et par ville, puis connectez-vous en un clic." },
            { i: Star, t: "Recommandations entre pros", d: "Donnez et recevez des recommandations qui renforcent votre réputation locale." },
            { i: Package, t: "Fournisseurs & collaborations", d: "Identifiez des fournisseurs locaux et montez des projets communs." }].map((f, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
              <f.i className="text-[#E05638] mb-2" size={22} />
              <div className="font-semibold text-slate-900">{f.t}</div>
              <p className="text-sm text-slate-500 mt-1">{f.d}</p>
            </div>
          ))}
        </div>

        {sectors.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-1">Parcourir par secteur</h2>
            <p className="text-slate-500 text-sm mb-4">Découvrez les professionnels d'Eure-et-Loir par activité.</p>
            <div className="flex flex-wrap gap-2" data-testid="netpub-sectors">
              {sectors.map((s) => (
                <Link key={s.id} to={`/reseau-pro/${s.slug}`} className="text-sm px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#E05638] text-slate-700 transition-colors" data-testid={`netpub-sector-${s.slug}`}>{s.name}</Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
